import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Megaphone, XCircle } from 'lucide-react';
import { integrationsApi } from '../api/integrationsApi';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { PageHeader } from '@/shared/ui';
import styles from './GoogleAdsSetupPage.module.css';

export function GoogleAdsSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tempToken = searchParams.get('t') ?? '';

  const [customerId, setCustomerId] = useState('');
  const [managerCustomerId, setManagerCustomerId] = useState('');
  const [error, setError] = useState('');

  const setupMutation = useMutation({
    mutationFn: () =>
      integrationsApi.googleAds.completeSetup(
        customerId,
        tempToken,
        managerCustomerId || undefined,
      ),
    onSuccess: () => {
      navigate('/settings/paid?connected=google_ads', { replace: true });
    },
    onError: (err: unknown) => {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Failed to connect Google Ads. Check your Customer ID and try again.',
      );
    },
  });

  if (!tempToken) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>
            <XCircle size={40} strokeWidth={1.5} aria-hidden />
          </div>
          <h1>Invalid Setup Link</h1>
          <p>This link is missing required authorization data.</p>
          <Button onClick={() => navigate('/settings/paid')}>Back to Paid settings</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <Megaphone size={40} strokeWidth={1.5} aria-hidden />
        </div>
        <PageHeader
          title="Connect Google Ads"
          subtitle="Authorization successful. Enter your Google Ads Customer ID to finish setup."
          align="center"
        />

        <div className={styles.hint}>
          <strong>Where to find your Customer ID:</strong>
          <ol>
            <li>Sign in to Google Ads</li>
            <li>Click the help icon in the top right</li>
            <li>
              Your Customer ID appears as <code>123-456-7890</code>
            </li>
          </ol>
          <p>
            If your account is managed by a Manager (MCC) account, also enter the Manager Account
            ID.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            setupMutation.mutate();
          }}
          className={styles.form}
        >
          <Input
            label="Customer ID"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder="123-456-7890"
            error={error}
            fullWidth
            required
          />
          <Input
            label="Manager Account ID (optional)"
            value={managerCustomerId}
            onChange={(e) => setManagerCustomerId(e.target.value)}
            placeholder="123-456-7890"
            fullWidth
          />
          <div className={styles.actions}>
            <Button type="button" variant="secondary" onClick={() => navigate('/settings/paid')}>
              Cancel
            </Button>
            <Button type="submit" disabled={!customerId.trim()} isLoading={setupMutation.isPending}>
              Connect
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
